import { getBrowserUserCookies } from '@opendoor/shared-ts/user-cookies/browser';

import { type ContextualData, type JourneyEventContextualData } from '../contextualData';
import { type JourneyClient, type JourneyClientInstance } from '../declarations/Clients';
import type { JourneyEventWrapper } from '../declarations/JourneyEvent';
import { type UserEvent } from '../declarations/UserEvent';
import { globalObservability } from '../global';
import { parseGclAgCookie, parseGclCookie } from './clickIdCookies';
import { trackErrorInDatadog } from './datadog';
import { getDeployEnv } from './deployEnv';
import type {
  JourneyAnalyticsCookieData,
  JourneyWebsiteSourceData,
} from './journeyProtobufEncoder';

type JourneyProtobufEncoderModule = typeof import('./journeyProtobufEncoder');

interface PendingJourneyEncodeOperation {
  name: 'trackUserEventInJourney' | 'trackJourneyEventInJourney';
  run: (module: JourneyProtobufEncoderModule) => void;
}

export const BASE_JOURNEY_ENDPOINT =
  getDeployEnv() === 'production'
    ? 'https://journey.apps.opendoor.com/_journey/api/v1'
    : 'https://journey.apps-staging.opendoor.com/_journey/api/v1';

let journeyProtobufEncoderModule: JourneyProtobufEncoderModule | undefined = undefined;
let journeyProtobufEncoderLoadPromise: Promise<void> | undefined = undefined;
let pendingJourneyEncodeOperations: PendingJourneyEncodeOperation[] = [];

const coerceError = (error: unknown): Error | undefined => {
  if (error === undefined) {
    return undefined;
  }
  if (error instanceof Error) {
    return error;
  }
  return new Error(String(error));
};

const reportJourneyEncodeError = (
  label: string,
  error: unknown,
  data?: Record<string, unknown>,
): void => {
  trackErrorInDatadog(label, coerceError(error), {
    data: {
      location: 'observability',
      ...data,
    },
  });
};

const runJourneyEncodeOperation = (
  operation: PendingJourneyEncodeOperation,
  encoderModule: JourneyProtobufEncoderModule,
): void => {
  try {
    operation.run(encoderModule);
  } catch (error) {
    reportJourneyEncodeError('failed to encode journey payload', error, {
      operation: operation.name,
    });
  }
};

const flushPendingJourneyEncodeOperations = (encoderModule: JourneyProtobufEncoderModule): void => {
  const operations = pendingJourneyEncodeOperations;
  pendingJourneyEncodeOperations = [];

  for (const operation of operations) {
    runJourneyEncodeOperation(operation, encoderModule);
  }
};

const loadJourneyProtobufEncoder = (): void => {
  if (journeyProtobufEncoderModule || journeyProtobufEncoderLoadPromise) {
    return;
  }

  // Load protobuf schemas only when Journey tracking is used to keep initial bundles lean.
  journeyProtobufEncoderLoadPromise = import('./journeyProtobufEncoder')
    .then((module) => {
      journeyProtobufEncoderModule = module;
      flushPendingJourneyEncodeOperations(module);
    })
    .catch((error) => {
      pendingJourneyEncodeOperations = [];
      reportJourneyEncodeError('failed to load journey protobuf encoder', error);
    })
    .finally(() => {
      journeyProtobufEncoderLoadPromise = undefined;
    });
};

const encodeJourneyPayload = (
  operation: PendingJourneyEncodeOperation['name'],
  callback: PendingJourneyEncodeOperation['run'],
): void => {
  if (journeyProtobufEncoderModule) {
    runJourneyEncodeOperation({ name: operation, run: callback }, journeyProtobufEncoderModule);
    return;
  }

  pendingJourneyEncodeOperations.push({ name: operation, run: callback });
  loadJourneyProtobufEncoder();
};

const getJourneyEndpoint = (journeyEndpoint?: string | null) => {
  if (journeyEndpoint) {
    // Uses the endpoint passed in by the client: because of the ingress the endpoint is different for each client
    return `${journeyEndpoint}/events`;
  } else {
    trackErrorInDatadog('the old journey endpoint is still being used', undefined, {
      data: {
        location: 'observability',
      },
    });
    // If no endpoint is passed - use the default endpoint. We will eventually remove this fall-back option
    return `${BASE_JOURNEY_ENDPOINT}/events`;
  }
};

export function initJourney(
  journeyEndpoint?: string | null,
  batchInterval: number = 5000,
): {
  userClient?: JourneyClientInstance;
  eventClient?: JourneyClientInstance;
} {
  // don't init journey client in server side rendering
  if (typeof window === 'undefined') {
    return {};
  }

  loadJourneyProtobufEncoder();

  const userEventClient = new JourneyClientImpl({
    batchInterval,
    journeyEndpoint: `${BASE_JOURNEY_ENDPOINT}/user_event_batch`,
    jsonKey: 'userEvents',
    maxNumRequest: 10,
  });
  userEventClient.start();

  const eventWrapperClient = new JourneyClientImpl({
    batchInterval,
    journeyEndpoint: getJourneyEndpoint(journeyEndpoint),
    jsonKey: 'eventWrappers',
    maxNumRequest: 20,
  });
  eventWrapperClient.start();

  // we use the visibilitychange event to determine if the page is unloaded in both mobile/web
  // https://www.igvita.com/2015/11/20/dont-lose-user-and-app-state-use-page-visibility/
  window.addEventListener('visibilitychange', () => {
    // we want to ensure we're sending events when the user switches tabs, apps, goes to homescreen, etc.
    if (document.visibilityState === 'hidden') {
      userEventClient?.send();
      eventWrapperClient?.send();
    }
  });

  // we use the beforeunload event to ensure events are sent when the page is about to reload
  // this catches cases where visibilitychange doesn't fire (e.g., window.location.reload())
  // Use fetch with keepalive for reliable page unload sending
  window.addEventListener('beforeunload', () => {
    userEventClient?.send();
    eventWrapperClient?.send();
  });

  return {
    userClient: userEventClient,
    eventClient: eventWrapperClient,
  };
}

export const getWebsiteSource = (
  overrides?: JourneyEventContextualData['websiteSourceContext'],
): JourneyWebsiteSourceData => {
  let url = '';
  let language: string[] = [];
  let userAgent = '';
  let host = '';
  let pathname = '';
  let queryMap: Record<string, string> = {};
  let screen: JourneyWebsiteSourceData['screen'] = undefined;
  let proxyPath = overrides?.proxyPath ?? undefined;
  let proxyQueryMap = overrides?.proxyQueryMap ?? {};
  let referrer = overrides?.referrer ?? undefined;

  if (typeof window !== 'undefined') {
    url = window.location.href;
    userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    language = typeof navigator !== 'undefined' ? [...navigator.languages] : [];
    host = window.location.host;
    pathname = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      queryMap[key] = queryMap[key] ? `${queryMap[key]},${value}` : value;
    });
    screen = {
      width: Math.round(window.innerWidth),
      height: Math.round(window.innerHeight),
      density: Math.round(window.devicePixelRatio),
    };
  }

  return {
    url,
    userAgent,
    host,
    pathname,
    queryMap,
    proxyQueryMap,
    referrer,
    proxyPath,
    screen,
    language,
  };
};

export const trackUserEventInJourney = (userEvent: UserEvent, contextualData: ContextualData) => {
  const anonymousId = getAnonymousID();
  const websiteSource = getWebsiteSource();
  if (!anonymousId) {
    trackErrorInDatadog('ajs_anonymous_id is empty or undefined', undefined, {
      data: {
        location: 'observability',
        anonymousId,
        event: userEvent,
      },
    });
  }

  const journeyUserClient = globalObservability.getJourneyUserClient();
  encodeJourneyPayload('trackUserEventInJourney', (encoderModule) => {
    journeyUserClient.enqueue?.(
      encoderModule.serializeUserEvent({
        userEvent,
        anonymousId,
        websiteSource,
        contextualData,
      }),
    );
  });
};

const getAnalyticsCookieData = (): JourneyAnalyticsCookieData => {
  if (typeof document === 'undefined') {
    return {};
  }

  const cookies = document.cookie.split(';').reduce(
    (acc, cookie) => {
      const [key, value] = cookie.trim().split('=');
      if (key && value) {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, string>,
  );

  return {
    fbBrowserId: cookies['_fbp'],
    fbClickId: cookies['_fbc'],
    redditBrowserId: cookies['_rdt_uuid'],
    tiktokBrowserId: cookies['_ttp'],
    nextdoorClickId: cookies['ndclid'],
    googleClickId: parseGclCookie(cookies['_gcl_aw']),
    googleWbraid: parseGclCookie(cookies['_gcl_gb']),
    googleGbraid: parseGclAgCookie(cookies['_gcl_ag']),
  };
};

export const trackJourneyEventInJourney = (
  event: Partial<JourneyEventWrapper>,
  contextualData: JourneyEventContextualData,
  sendJourneyEventsInDev?: boolean,
  clientEventId?: string,
) => {
  const anonymousId = getAnonymousID();
  const websiteSource = getWebsiteSource(contextualData.websiteSourceContext);

  if (!anonymousId) {
    if (sendJourneyEventsInDev) {
      console.log(
        'Please copy your odAuthAid cookie value and set it as the ajs_anonymous_id cookie value for your localhost to send journey events',
      );
    } else {
      trackErrorInDatadog('ajs_anonymous_id is empty or undefined', undefined, {
        data: {
          location: 'observability',
          anonymousId,
          event,
        },
      });
    }
  }

  const analyticsCookieData = getAnalyticsCookieData();
  const journeyEventClient = globalObservability.getJourneyEventClient();
  encodeJourneyPayload('trackJourneyEventInJourney', (encoderModule) => {
    journeyEventClient.enqueue?.(
      encoderModule.serializeJourneyEvent({
        event,
        contextualData,
        anonymousId,
        websiteSource,
        analyticsCookieData,
        clientEventId,
      }),
    );
  });
};

const getAnonymousID = (): string | undefined => getBrowserUserCookies().odAuthAid ?? undefined;

// journey http endpoint batch size limit is 512KB
const MAX_PAYLOAD_SIZE = 512;

function computeSizeInKilobytes(buffer: string[]): number {
  const size = buffer.map((s) => s.length).reduce((prev, cur) => prev + cur, 0);
  return size / 1024;
}

/**
 * Checks if the payload is over or close to (within 50B of) the maximum payload size.
 */
function isApproachingBatchAPILimit(buffer: string[]): boolean {
  return computeSizeInKilobytes(buffer) >= MAX_PAYLOAD_SIZE - 50;
}

/**
 * TODO (billy): add to `shared-ts`
 * A wrapper around `fetch` with 5 retries at 500ms intervals
 */
const fetchWithRetry = async (url: string, init: RequestInit): Promise<void> => {
  let retries = 5;
  const retryDelay = 500;
  let response: Response | undefined = undefined;

  while (retries > 0) {
    try {
      response = await fetch(url, init);
      const statusCode = response.status;
      if (statusCode >= 200 && statusCode < 300) {
        return;
      }
      if (statusCode >= 500 && retries >= 1) {
        // sleep the specified amount of time, then retry
        await new Promise((r) => setTimeout(r, retryDelay));
        retries = retries - 1;
      } else {
        break;
      }
    } catch (err) {
      if (retries >= 1) {
        // sleep the specified amount of time, then retry
        await new Promise((r) => setTimeout(r, retryDelay));
        retries = retries - 1;
      }
    }
  }

  if (init.headers?.toString().includes('Puppeteer')) {
    // do not log errors for puppeteer requests
    return;
  }

  // no more retries left, report to Sentry
  const sentry = globalObservability.getSentryClient();
  const status = response?.status;
  // 4xx responses are expected client-side rejections (e.g. 403 from WAF/bot
  // filtering) and not actionable, so report them as a message rather than an
  // exception. Server errors and network failures remain exceptions.
  const isClientError = status !== undefined && status >= 400 && status < 500;
  sentry.withScope?.((scope) => {
    scope.setExtra('body', init.body);
    const message = `failed to track events in journey,statusCode: ${status}, response available: ${!!response}`;
    if (isClientError) {
      sentry.captureMessage?.(message);
    } else {
      sentry.captureException?.(message);
    }
  });
};

interface JourneyClientOpts {
  batchInterval: number;
  journeyEndpoint: string;
  jsonKey: string;
  maxNumRequest: number;
}

export class JourneyClientImpl implements JourneyClient {
  private buffer: string[];
  private readonly batchInterval: number;
  private readonly journeyEndpoint: string;
  private readonly jsonKey: string;
  private readonly maxNumRequest: number;
  private timer: ReturnType<typeof setInterval> | undefined;
  constructor({ batchInterval, journeyEndpoint, jsonKey, maxNumRequest }: JourneyClientOpts) {
    this.buffer = [];
    this.batchInterval = batchInterval;
    this.journeyEndpoint = journeyEndpoint;
    this.jsonKey = jsonKey;
    this.maxNumRequest = maxNumRequest;
  }

  start() {
    this.timer = setInterval(() => this.send(), this.batchInterval);
  }

  stop() {
    this.timer && clearInterval(this.timer);
  }

  send() {
    if (this.buffer.length === 0) {
      return;
    }
    fetchWithRetry(this.journeyEndpoint, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Journey-Client-Name': 'observability',
        'X-Journey-Client-Version': '1.0',
      },
      // Using keepalive:true ensures that the request is made even if the page is being unloaded.
      // There are actually benefits to using keep-alive true: https://www.stackpath.com/edge-academy/what-is-keep-alive/
      keepalive: true,
      method: 'POST',
      body: `{"${this.jsonKey}":[${this.buffer.join(',')}]}`,
    });
    this.buffer = [];
  }

  enqueue(json: string): void {
    this.buffer.push(json);
    if (this.buffer.length >= this.maxNumRequest || isApproachingBatchAPILimit(this.buffer)) {
      this.send();
    }
  }
}
